import { useQuery } from '@tanstack/react-query';
import { useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import { Link } from 'react-router-dom';
import { appRoutes } from '../../app-routes';
import { MonacoJsonEditor } from '../../components/MonacoJsonEditor';
import { credentialDisplayName, partnerApi, type ToolDefinitionInput } from '../../lib/api';
import { RequestPreview } from './RequestPreview';
import { ScopeHelp } from './ScopeHelp';
import { ToolInputsTable } from './ToolInputsTable';
import { TOOL_EXAMPLES } from './tool-examples';
import { normalizeToolScope, TOOL_SCOPE_OPTIONS } from './tool-scopes';
import {
  DEFAULT_CREDENTIAL_SLOT,
  deriveAnnotations,
  isTableRepresentable,
  requestSchemaToRows,
  rowsToRequestMapping,
  rowsToRequestSchema,
  tableRepresentabilityIssue,
  validateInputRows,
  validateToolName,
  type ToolAuthConfig,
  type ToolInputRow,
} from './tool-inputs';
import styles from './ToolForm.module.css';

interface ToolFormProps {
  draft: ToolDefinitionInput;
  onChange: (next: ToolDefinitionInput) => void;
  isEditing?: boolean;
  onValidityChange?: (isValid: boolean) => void;
  /** Canonical `<partnerId>_<slug>` name, available once the tool is saved. */
  canonicalName?: string;
}

const HTTP_METHODS = ['GET', 'POST', 'PUT', 'PATCH', 'DELETE'] as const;

/** How the stored secret is attached to the request. `api_key` collapses into the header option. */
const SEND_AS_OPTIONS = [
  { value: 'custom_header', label: 'In a header' },
  { value: 'bearer', label: 'As a Bearer token' },
  { value: 'basic', label: 'As Basic auth' },
] as const;

const normalizeAuthType = (authType: string): string =>
  authType === 'api_key' ? 'custom_header' : authType;

export const ToolForm = ({
  draft,
  onChange,
  isEditing = false,
  onValidityChange,
  canonicalName,
}: ToolFormProps) => {
  const { data: credentialsData } = useQuery({
    queryKey: ['ai-credentials'],
    queryFn: () => partnerApi.getCredentials(),
  });
  const credentials = credentialsData?.credentials ?? [];

  const set = <K extends keyof ToolDefinitionInput>(key: K, value: ToolDefinitionInput[K]) => {
    onChange({ ...draft, [key]: value });
  };

  // The draft is the source of truth, but rows are buffered locally so a transient blank or
  // duplicate name doesn't collapse when serialized into request_schema's object keys.
  const [rows, setRows] = useState<ToolInputRow[]>(() =>
    requestSchemaToRows(draft.request_schema, draft.request_mapping, draft.http_method),
  );
  const [rawMode, setRawMode] = useState(
    () => !isTableRepresentable(draft.request_schema, draft.request_mapping, draft.http_method),
  );
  // Canonical form of the last schema we emitted; lets us ignore our own writes and reseed only
  // when the schema changes from outside (server-seed when editing, or applying an example).
  const emittedSchemaRef = useRef(JSON.stringify(draft.request_schema));

  useEffect(() => {
    const incoming = JSON.stringify(draft.request_schema);
    if (incoming !== emittedSchemaRef.current) {
      emittedSchemaRef.current = incoming;
      setRows(requestSchemaToRows(draft.request_schema, draft.request_mapping, draft.http_method));
      setRawMode(!isTableRepresentable(draft.request_schema, draft.request_mapping, draft.http_method));
    }
  }, [draft.request_schema, draft.request_mapping, draft.http_method]);

  const rowIssues = useMemo(() => validateInputRows(rows), [rows]);
  const nameError = isEditing ? null : validateToolName(draft.display_name);

  // Raw-mode JSON validity (escape hatch only).
  const [invalidJsonKeys, setInvalidJsonKeys] = useState<Set<string>>(new Set());
  const reportFieldValidity = (key: string, isValid: boolean) => {
    setInvalidJsonKeys(prev => {
      const wasInvalid = prev.has(key);
      const validityUnchanged = isValid === !wasInvalid;
      if (validityUnchanged) return prev;
      const next = new Set(prev);
      if (isValid) next.delete(key);
      else next.add(key);
      return next;
    });
  };

  useEffect(() => {
    const tableValid = rawMode || rowIssues.length === 0;
    onValidityChange?.(!nameError && tableValid && invalidJsonKeys.size === 0);
  }, [nameError, rawMode, rowIssues.length, invalidJsonKeys, onValidityChange]);

  const setRequestSchema = (schema: Record<string, unknown>) => {
    emittedSchemaRef.current = JSON.stringify(schema);
    set('request_schema', schema);
  };

  const commitRows = (nextRows: ToolInputRow[]) => {
    setRows(nextRows);
    const schema = rowsToRequestSchema(nextRows);
    emittedSchemaRef.current = JSON.stringify(schema);
    onChange({
      ...draft,
      request_schema: schema,
      request_mapping: rowsToRequestMapping(nextRows, draft.http_method),
    });
  };

  const setMethod = (method: string) => {
    onChange({
      ...draft,
      http_method: method,
      request_mapping: rawMode ? draft.request_mapping : rowsToRequestMapping(rows, method),
      annotations: deriveAnnotations(method),
    });
  };

  const authConfig = (draft.auth_config ?? {}) as ToolAuthConfig;
  const credentialSlot = authConfig.credentialSlot || DEFAULT_CREDENTIAL_SLOT;
  const selectedCredentialId = draft.credentials?.[credentialSlot] ?? '';
  const setCredential = (credentialId: string) =>
    onChange({
      ...draft,
      auth_config: { ...authConfig, credentialSlot },
      credentials: { ...draft.credentials, [credentialSlot]: credentialId },
    });
  const setSendAs = (authType: string) => set('auth_type', authType);
  const setHeaderName = (headerName: string) =>
    set('auth_config', { ...authConfig, credentialSlot, headerName });
  const showHeaderName = normalizeAuthType(draft.auth_type) === 'custom_header';

  const representabilityIssue = tableRepresentabilityIssue(
    draft.request_schema,
    draft.request_mapping,
    draft.http_method,
  );
  const canSwitchToSimple = representabilityIssue === null;

  const switchToSimple = () => {
    if (!rawMode) return;
    setRows(requestSchemaToRows(draft.request_schema, draft.request_mapping, draft.http_method));
    setRawMode(false);
  };

  const isPristine = !isEditing && !draft.display_name && !draft.base_url && rows.length === 0;

  return (
    <div className={styles.form}>
      {isPristine && (
        <aside className={styles.examples}>
          <p className={styles.examplesLead}>New to this? Start from an example and edit it:</p>
          <div className={styles.examplesGrid}>
            {TOOL_EXAMPLES.map(example => (
              <button
                key={example.key}
                type="button"
                onClick={() => onChange(example.draft)}
                className={styles.exampleButton}>
                <span className={styles.exampleName}>{example.label}</span>
                <span className={styles.exampleBlurb}>{example.blurb}</span>
              </button>
            ))}
          </div>
        </aside>
      )}

      <Section
        step={1}
        title="Name and describe the tool"
        subtitle="The agent reads the description to decide when to call this tool.">
        {isEditing && canonicalName ? (
          <Field label="Tool name" hint="The runtime name agents call. Locked after creation.">
            <input type="text" value={canonicalName} readOnly className={styles.monoInput} />
          </Field>
        ) : (
          <Field
            label="Name"
            hint="Lowercase snake_case, starting with a letter (e.g. upsert_customer).">
            <input
              type="text"
              value={draft.display_name}
              onChange={e => set('display_name', e.target.value)}
              disabled={isEditing}
              className={styles.monoInput}
              placeholder="upsert_customer"
              aria-invalid={Boolean(nameError)}
            />
            {nameError && <span className={styles.fieldError}>{nameError}</span>}
          </Field>
        )}
        <Field label="Description" hint="Tell the agent what this does and when to use it.">
          <textarea
            value={draft.description}
            onChange={e => set('description', e.target.value)}
            className={styles.textarea}
            rows={8}
            placeholder="Create or update a customer profile in our system. Use this when an agent needs to persist customer identity or profile details gathered in conversation. This is an upsert operation and may be called multiple times in one conversation as more details become available. Call it whenever the required identifier inputs in the schema are available and there is a new usable customer detail to save. Omit unknown optional fields and never send placeholders. A successful response means our system accepted the supplied fields; it does not necessarily mean the profile is complete."
          />
        </Field>
      </Section>

      <Section
        step={2}
        title="Point it at your API"
        subtitle="When the agent runs this tool, we send an HTTP request to this URL.">
        <Field label="Method">
          <select
            value={draft.http_method}
            onChange={e => setMethod(e.target.value)}
            className={styles.monoSelect}>
            {HTTP_METHODS.map(method => (
              <option key={method} value={method}>
                {method}
              </option>
            ))}
          </select>
        </Field>
        <Field label="Base URL">
          <input
            type="text"
            value={draft.base_url}
            onChange={e => set('base_url', e.target.value)}
            className={styles.monoInput}
            placeholder="https://api.yourcompany.com/v1"
          />
        </Field>
        <Field label="Path" hint="Appended to the base URL.">
          <input
            type="text"
            value={draft.path_template}
            onChange={e => set('path_template', e.target.value)}
            className={styles.monoInput}
            placeholder="/customers"
          />
        </Field>
      </Section>

      <Section
        step={3}
        title="Authenticate the request"
        subtitle="Pick the credential we send with the request, and how to attach it.">
        <Field label="Credential">
          {credentials.length === 0 ? (
            <p className={styles.helpText}>
              No credentials yet.{' '}
              <Link to={appRoutes.AI_CREDENTIALS} className={styles.subtitleLink}>
                Add a credential first
              </Link>
              , then come back.
            </p>
          ) : (
            <select
              value={selectedCredentialId}
              onChange={e => setCredential(e.target.value)}
              className={styles.select}>
              <option value="">Pick a credential…</option>
              {credentials.map(credential => (
                <option key={credential.id} value={credential.id}>
                  {credentialDisplayName(credential)}
                </option>
              ))}
            </select>
          )}
        </Field>
        <Field label="Send it">
          <select
            value={normalizeAuthType(draft.auth_type)}
            onChange={e => setSendAs(e.target.value)}
            className={styles.select}>
            {SEND_AS_OPTIONS.map(option => (
              <option key={option.value} value={option.value}>
                {option.label}
              </option>
            ))}
          </select>
        </Field>
        {showHeaderName && (
          <Field label="Header name">
            <input
              type="text"
              value={authConfig.headerName ?? ''}
              onChange={e => setHeaderName(e.target.value)}
              className={styles.monoInput}
              placeholder="x-api-key"
            />
          </Field>
        )}
      </Section>

      <Section
        step={4}
        title="Define the tool's inputs"
        subtitle="Each row is one input — a value we send to your API every time the agent runs this tool.">
        <div className={styles.editorModeRow}>
          <button
            type="button"
            aria-pressed={!rawMode}
            disabled={rawMode && !canSwitchToSimple}
            onClick={switchToSimple}>
            Simple
          </button>
          <button type="button" aria-pressed={rawMode} onClick={() => setRawMode(true)}>
            JSON
          </button>
          {rawMode && representabilityIssue && (
            <span className={styles.editorModeReason}>{representabilityIssue}</span>
          )}
        </div>
        {rawMode ? (
          <>
            <JsonField
              label="request_schema"
              hint="JSON Schema describing the inputs the agent provides."
              value={draft.request_schema}
              onChange={value => setRequestSchema(value as Record<string, unknown>)}
              onValidityChange={value => reportFieldValidity('request_schema', value)}
              height="200px"
            />
            <JsonField
              label="request_mapping"
              hint="Maps inputs into the request. Group fields under body, query, headers, or path."
              value={draft.request_mapping}
              onChange={value => set('request_mapping', value as Record<string, unknown>)}
              onValidityChange={value => reportFieldValidity('request_mapping', value)}
              height="200px"
            />
          </>
        ) : (
          <ToolInputsTable
            rows={rows}
            issues={rowIssues}
            method={draft.http_method}
            onChange={commitRows}
          />
        )}
      </Section>

      <Section title="Request preview" subtitle="Exactly what we send when the agent runs the tool.">
        <RequestPreview draft={draft} />
      </Section>

      <Section
        title="Who can call this tool"
        subtitle="The minimum caller verification level before the agent may run this tool.">
        <div className={styles.field}>
          <span className={styles.fieldLabelRow}>
            <label htmlFor="tool-required-scope" className={styles.fieldLabel}>
              Required scope
            </label>
            <ScopeHelp />
          </span>
          <span className={styles.fieldHint}>
            Use Verified for tools that share personal info or change an account.
          </span>
          <div className={styles.fieldControl}>
            <select
              id="tool-required-scope"
              value={normalizeToolScope(draft.required_scope)}
              onChange={e => set('required_scope', e.target.value)}
              className={styles.select}>
              {TOOL_SCOPE_OPTIONS.map(option => (
                <option key={option.value} value={option.value}>
                  {option.label}
                </option>
              ))}
            </select>
          </div>
        </div>
      </Section>

      {isEditing && (
        <Section title="Status" subtitle="Inactive tools are hidden from agents.">
          <Field label="Status">
            <select
              value={draft.status}
              onChange={e => set('status', e.target.value as 'active' | 'inactive')}
              className={styles.select}>
              <option value="active">active</option>
              <option value="inactive">inactive</option>
            </select>
          </Field>
        </Section>
      )}
    </div>
  );
};

interface SectionProps {
  step?: number;
  title: string;
  subtitle?: ReactNode;
  children: ReactNode;
}

const Section = ({ step, title, subtitle, children }: SectionProps) => (
  <fieldset className={styles.section}>
    <legend className={styles.legend}>
      {step !== undefined && <span className={styles.stepBadge}>{step}</span>}
      {title}
    </legend>
    {subtitle && <p className={styles.sectionSubtitle}>{subtitle}</p>}
    <div className={styles.sectionBody}>{children}</div>
  </fieldset>
);

interface FieldProps {
  label: string;
  hint?: string;
  children: ReactNode;
}

const Field = ({ label, hint, children }: FieldProps) => (
  <label className={styles.field}>
    <span className={styles.fieldLabel}>{label}</span>
    {hint && <span className={styles.fieldHint}>{hint}</span>}
    <div className={styles.fieldControl}>{children}</div>
  </label>
);

interface JsonFieldProps {
  label: string;
  hint?: string;
  value: unknown;
  onChange: (parsed: unknown) => void;
  onValidityChange?: (isValid: boolean) => void;
  height: string;
}

const JsonField = ({ label, hint, value, onChange, onValidityChange, height }: JsonFieldProps) => {
  const incomingCanonical = useMemo(() => JSON.stringify(value), [value]);
  const [text, setText] = useState(() => JSON.stringify(value, null, 2));
  const [isValid, setIsValid] = useState(true);

  // Comparing canonical shapes lets a parent that re-stringifies our parsed value with
  // `null, 2` round-trip without clobbering the user's exact text. Only externally-driven
  // value changes (e.g. seeding from server) produce a different canonical form and reseed.
  const lastReportedCanonicalRef = useRef(incomingCanonical);

  useEffect(() => {
    if (incomingCanonical !== lastReportedCanonicalRef.current) {
      lastReportedCanonicalRef.current = incomingCanonical;
      setText(JSON.stringify(value, null, 2));
      setIsValid(true);
      onValidityChange?.(true);
    }
  }, [incomingCanonical, value, onValidityChange]);

  const handleChange = (next: string) => {
    setText(next);
    try {
      const parsed = JSON.parse(next);
      lastReportedCanonicalRef.current = JSON.stringify(parsed);
      setIsValid(true);
      onValidityChange?.(true);
      onChange(parsed);
    } catch {
      setIsValid(false);
      onValidityChange?.(false);
    }
  };

  return (
    <Field label={label} hint={hint}>
      <MonacoJsonEditor value={text} onChange={handleChange} height={height} />
      {!isValid && (
        <p className={styles.jsonError} role="alert">
          Invalid JSON — fix before saving.
        </p>
      )}
    </Field>
  );
};
